export const PRIVACY_OPT_OUT_STORAGE_KEY = "betterintentions.privacyOptOut";
export const PRIVACY_OPT_OUT_SOURCE_GPC = "gpc";
export const PRIVACY_OPT_OUT_SOURCE_MANUAL = "manual";

function getBrowserStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
}

function getBrowserNavigator() {
  if (typeof navigator === "undefined") return null;
  return navigator;
}

// Global Privacy Control (GPC) is a browser-level opt-out-of-sale/sharing
// signal that several US state privacy laws (CPRA §1798.135(e), 11 CCR 7025,
// and the CO/CT/MT/TX/OR universal opt-out rules) require businesses to honor.
export function isGpcSignalActive(navigatorRef = getBrowserNavigator()) {
  return navigatorRef?.globalPrivacyControl === true;
}

export function getStoredOptOut(storage = getBrowserStorage()) {
  if (!storage) return false;
  return storage.getItem(PRIVACY_OPT_OUT_STORAGE_KEY) === "true";
}

export function setStoredOptOut(value, storage = getBrowserStorage()) {
  if (!storage) return;
  if (value) {
    storage.setItem(PRIVACY_OPT_OUT_STORAGE_KEY, "true");
  } else {
    storage.removeItem(PRIVACY_OPT_OUT_STORAGE_KEY);
  }
}

export function getEffectiveOptOut({
  profile = null,
  navigatorRef = getBrowserNavigator(),
  storage = getBrowserStorage(),
} = {}) {
  // An active GPC signal takes precedence over any manual opt-in
  // (11 CCR 7025(c)).
  if (isGpcSignalActive(navigatorRef)) return true;
  if (profile?.privacyOptOut === true) return true;
  return getStoredOptOut(storage);
}

// Mandatory gate for any future analytics, advertising, or data-sharing
// integration: initialization of such SDKs must consult this and skip
// loading when it returns false.
export function isTrackingAllowed(options = {}) {
  return !getEffectiveOptOut(options);
}

// Propagates an opt-out to the authenticated user's profile so it is recorded
// account-wide (11 CCR 7025(b): the signal applies to the consumer profile
// associated with the browser). Covers both an active GPC signal and a
// locally stored opt-out (recorded while anonymous, or while the signal was
// active earlier — opt-outs persist until the user revokes them). No-op when
// there is nothing to propagate, the profile already records an opt-out, or
// the mutation isn't available yet (backend schema not deployed). Returns the
// refreshed profile when a sync happened, otherwise null.
export async function syncPrivacyOptOutToProfile({
  cognitoId,
  profile = null,
  setPrivacyOptOutMutation,
  navigatorRef = getBrowserNavigator(),
  storage = getBrowserStorage(),
} = {}) {
  const gpcActive = isGpcSignalActive(navigatorRef);
  if (!gpcActive && !getStoredOptOut(storage)) return null;

  const normalizedCognitoId = String(cognitoId || "").trim();
  if (!normalizedCognitoId || typeof setPrivacyOptOutMutation !== "function") {
    return null;
  }
  if (profile?.privacyOptOut === true) return null;

  const response = await setPrivacyOptOutMutation({
    cognitoId: normalizedCognitoId,
    optOut: true,
    source: gpcActive
      ? PRIVACY_OPT_OUT_SOURCE_GPC
      : PRIVACY_OPT_OUT_SOURCE_MANUAL,
  });
  if (response?.errors?.length) {
    throw new Error(response.errors.map((entry) => entry.message).join(", "));
  }
  return response?.data || null;
}
